import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Paragraph,
	SettingsEmptyState,
	SettingsSection,
	SettingsTable,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import LearningTypeEdit from './LearningTypeEdit';

const AnchorElement = styled(Link)`
	color: ${tokens.brand.text};
	text-decoration: underline;

	${Paragraph} {
		color: ${tokens.brand.text};
	}
`;

export default function LearningTypes() {
	const { t } = useTranslation();

	const [adding, setAdding] = useState(false);
	const [showArchived, setShowArchived] = useState(false);

	const queries = {
		active: graphql.useLearningTypesQuery({ variables: { first: 1000 } }),
		archive: graphql.useLearningTypesLazyQuery({ variables: { isArchived: true, first: 1000 } }),
	};

	const learningTypes = queries.active.data?.learningTypes;
	const archivedLearningTypes = queries.archive[1]?.data?.learningTypes;

	const onRefresh = async () => {
		await queries.active.refetch();
		await queries.archive[1]?.refetch?.();
		setAdding(false);
	};

	return (
		<>
			<SettingsSection first>
				<SettingsTable
					title={t('common.settings.learning.learningTypes.title')}
					description={t('common.settings.learning.learningTypes.description')}
					toolbar={
						<Button
							icon={<AddIcon />}
							title={t('common.add')}
							onClick={() => setAdding(true)}
							type="secondary"
						/>
					}
				>
					{learningTypes ? (
						learningTypes.edges.length > 0 || adding ? (
							<>
								{learningTypes.edges.map(({ node }) => (
									<LearningTypeEdit
										key={node.id}
										learningType={node as graphql.LearningType}
										onRefresh={onRefresh}
									/>
								))}
								{adding && <LearningTypeEdit onRefresh={onRefresh} />}
							</>
						) : (
							<SettingsEmptyState>
								{t('common.settings.learning.learningTypes.empty')}
							</SettingsEmptyState>
						)
					) : (
						<Flex my="22px" mx={3}>
							<Spinner />
						</Flex>
					)}
				</SettingsTable>
			</SettingsSection>

			{showArchived ? (
				<SettingsSection>
					<SettingsTable
						divided
						title={t('common.settings.learning.learningTypes.archived.title')}
						description={t('common.settings.learning.learningTypes.archived.description')}
					>
						{archivedLearningTypes ? (
							archivedLearningTypes.edges.length > 0 || adding ? (
								<>
									{archivedLearningTypes.edges.map(({ node }) => (
										<LearningTypeEdit
											key={node.id}
											learningType={node as graphql.LearningType}
											onRefresh={onRefresh}
										/>
									))}
									{adding && <LearningTypeEdit onRefresh={onRefresh} />}
								</>
							) : (
								<SettingsEmptyState>
									{t('common.settings.learning.learningTypes.archived.empty')}
								</SettingsEmptyState>
							)
						) : (
							<Spinner />
						)}
					</SettingsTable>
				</SettingsSection>
			) : (
				<Flex px={3}>
					<AnchorElement
						to="/settings/learning/types"
						onClick={() => {
							setShowArchived(true);
							queries.archive[0]();
						}}
					>
						<Paragraph>
							{t('common.settings.learning.learningTypes.archived.button.title')}
						</Paragraph>
					</AnchorElement>
				</Flex>
			)}
		</>
	);
}
